import { gql, DocumentNode } from '@apollo/client';
import { Box, Paper, Typography } from '@mui/material';
import React from 'react';
import { DatePicker } from '../../DatePicker';
import { TitleField } from '../../TitleField';
import {
	InstallmentStatus,
	InvoiceEditPresentationInvoiceFragment,
	InvoiceStatus,
} from '#types';
import Contact from '../../Contact';
import styles from './InvoiceEditPresentation.module.scss';
import {
	InvoiceEditApplyTemplate,
	InvoiceEditApplyTemplateProps,
} from './InvoiceEditApplyTemplate';
import { NewContact } from '../../../hooks/useContacts';

const invoiceEditPresentationInvoiceFragment = gql`
	fragment invoiceEditPresentationInvoice on Invoice {
		id
		title
		eventDate
		...invoiceEditApplyTemplateInvoice
	}
	${ InvoiceEditApplyTemplate.fragments.invoiceEditApplyTemplateInvoiceFragment }
`;

export type InvoiceEditPresentationProps = {
	invoiceExists: boolean;
	invoice?: Omit<InvoiceEditPresentationInvoiceFragment, 'id'> & {
		id?: string;
	};
	setInvoice: React.Dispatch<
		React.SetStateAction<
			Omit<InvoiceEditPresentationInvoiceFragment, 'id'> & { id?: string }
		>
	>;
	titleValidation?: string;
	eventDateValidation?: string;
	onBlur: () => boolean | undefined;
	contactOptional: boolean;
	validationEnabled?: boolean;
	isVisible: boolean;
	children?: React.ReactNode;
} & InvoiceEditApplyTemplateProps;

type InvoiceEditPresentationPropsWithFragment =
	React.FC<InvoiceEditPresentationProps> & {
		fragments: {
			invoiceEditPresentationInvoiceFragment: DocumentNode;
		};
	};

export const InvoiceEditPresentation: InvoiceEditPresentationPropsWithFragment =
	( {
		invoiceExists,
		invoice,
		setInvoice,
		invoiceTemplate,
		setInvoiceTemplate,
		titleValidation,
		eventDateValidation,
		onBlur,
		validationEnabled,
		contactOptional,
		isVisible,
		children,
	} ) => {
		const handleContactChange = React.useCallback(
			( contact?: NewContact & { id?: string } ) => {
				if ( contact ) {
					setInvoice( ( prevState ) => ( {
						...prevState,
						contact,
					} ) );
				}
			},
			[ setInvoice ]
		);

		const contactIsDisabled =
			( invoice?.status &&
				invoice.status !== InvoiceStatus.Draft &&
				invoice?.paymentInstallments &&
				!invoice?.paymentInstallments.every(
					( paymentInstallment ) =>
						paymentInstallment.status === InstallmentStatus.Unpaid
				) ) ||
			false;

		const eventDateIsDisabled =
			( invoice?.status &&
				[
					InvoiceStatus.Disputed,
					InvoiceStatus.Hidden,
					InvoiceStatus.Void,
				].includes( invoice.status ) ) ||
			false;

		return (
			<Box className={ styles.fakePaperTop }>
				<Paper className={ styles.container }>
					<TitleField
						className={ styles.title }
						label='Invoice name'
						value={ invoice?.title || '' }
						onChange={ ( e ) =>
							setInvoice( ( prevState ) => ( {
								...prevState,
								title: e.target.value,
							} ) )
						}
						error={ !!titleValidation }
						helperText={ titleValidation }
						onBlur={ onBlur }
						autoFocus
					/>
					<DatePicker
						className={ styles.eventDate }
						slotProps={ {
							textField: {
								variant: 'standard',
								slotProps: { htmlInput: { 'data-cy': 'invoice-event-date' } },
							},
						} }
						label='Event date (optional)'
						value={ invoice?.eventDate }
						onChange={ ( eventDate ) =>
							setInvoice( ( prevState ) => ( { ...prevState, eventDate } ) )
						}
						onAccept={ ( eventDate ) =>
							setInvoice( ( prevState ) => ( { ...prevState, eventDate } ) )
						}
						onBlur={ onBlur }
						disabled={ eventDateIsDisabled }
						errorText={ eventDateValidation }
					/>
					<InvoiceEditApplyTemplate
						className={ styles.applyTemplate }
						invoiceExists={ invoiceExists }
						invoice={ invoice }
						invoiceTemplate={ invoiceTemplate }
						setInvoiceTemplate={ setInvoiceTemplate }
					/>
					<Box className={ styles.contactContainer }>
						<Typography variant='h6'>Bill to</Typography>
						<Contact
							isVisible={ isVisible }
							className={ styles.contact }
							contactIndex={ 0 }
							validationEnabled={ validationEnabled || false }
							optional={ contactOptional }
							disabled={ contactIsDisabled }
							descriptiveLabels={ false }
							onChange={ handleContactChange }
						/>
					</Box>
					{ children }
				</Paper>
			</Box>
		);
	};

InvoiceEditPresentation.fragments = {
	invoiceEditPresentationInvoiceFragment,
};
